import { useCallback, useEffect, useState } from "react";

import {
  type GridRowId,
  type DataGridProProps,
  type GridColDef,
} from "@mui/x-data-grid-pro";
import {
  CaretRightIcon,
  CheckIcon,
  COLORS,
  EditIcon,
  theme,
} from "@stacklet/ui";
import pluralize from "pluralize";
import { graphql, usePaginationFragment } from "react-relay";

import { TableLink } from "app/components/links";
import { Link } from "app/components/links/Link";
import { RbacTargetChipGroup } from "app/components/rbac/RbacTargetChipGroup";
import { TruncatedText } from "app/components/table/TruncatedText";
import { XGridTable } from "app/components/table/XGridTable";
import { useLoadNext, useHasScopePermission } from "app/hooks";
import { useClientSideSortableGrid } from "app/hooks/grid/useClientSideSortableGrid";

import {
  type GroupsTable_query$data,
  type GroupsTable_query$key,
} from "./__generated__/GroupsTable_query.graphql";
import { type GroupsTablePaginationQuery } from "./__generated__/GroupsTablePaginationQuery.graphql";

type UserGroupNode = NonNullable<
  GroupsTable_query$data["userGroups"]["edges"]
>[number]["node"];
import type { FilterElementInput } from "../__generated__/GroupsSettingsRootQuery.graphql";

interface GroupRow {
  id: string;
  uuid: string;
  name: string;
  displayName: string;
  sso: boolean;
  role: string;
  /** True when the group has more role assignments than we fetched (capped),
   * so the displayed role count is a lower bound (rendered as "N+"). */
  hasMoreRoles: boolean;
  target: string;
  description: string;
  roleAssignments: {
    role: string;
    target: string;
    description: string;
  }[];
}

// Keep in sync with the `roleAssignments(first: ...)` cap in the fragment
// below. Used as a fallback when the connection doesn't report pageInfo.total.
const ROLE_ASSIGNMENTS_PAGE_SIZE = 100;

interface RoleAssignmentRow {
  id: string;
  name: string;
  role: string;
  targetList: string[];
  description: string;
}

interface Props {
  queryRef: GroupsTable_query$key;
  expanded?: boolean;
  filterElement: FilterElementInput | undefined;
  order: string;
}

export function Groups({ queryRef, expanded }: Props) {
  const { data, hasNext, loadNext, isLoadingNext } = usePaginationFragment<
    GroupsTablePaginationQuery,
    GroupsTable_query$key
  >(
    graphql`
      fragment GroupsTable_query on Query
      @argumentDefinitions(
        first: { type: "Int" }
        after: { type: "String" }
        last: { type: "Int" }
        before: { type: "String" }
        filterElement: { type: "FilterElementInput" }
        order: { type: "String" }
      )
      @refetchable(queryName: "GroupsTablePaginationQuery") {
        userGroups(
          first: $first
          last: $last
          after: $after
          before: $before
          filterElement: $filterElement
          order: $order
        ) @connection(key: "GroupsTable_userGroups") {
          edges {
            node {
              id
              uuid
              name
              displayName
              source {
                __typename
              }
              roleAssignments(first: 100) {
                edges {
                  node {
                    id
                    role {
                      name
                      permissions
                    }
                    target {
                      __typename
                      roleAssignmentTarget
                    }
                  }
                }
                pageInfo {
                  total
                }
              }
            }
          }
        }
      }
    `,
    queryRef,
  );

  const pxToRem = theme.typography.pxToRem;

  const loadMore = useLoadNext(hasNext, loadNext);

  // Use client-side sorting with URL persistence
  const [sortModel, setSortModel] = useClientSideSortableGrid();

  const hasPermission = useHasScopePermission();
  const canManageGroups = hasPermission("system", "EDIT");

  const columns: GridColDef[] = [
    {
      headerName: "Name",
      field: "name",
      type: "string",
      width: 200,
      minWidth: 200,
      renderCell: (params) => {
        const { uuid, name, displayName } = params.row;
        return (
          <TableLink
            name={displayName || name}
            to={`/settings/access-control/groups/${uuid}`}
          />
        );
      },
    },

    {
      headerName: "SSO",
      field: "sso",
      width: 80,
      minWidth: 80,
      sortable: false,
      renderCell: ({ value }) =>
        value ? (
          <CheckIcon aria-label="group" style={{ color: COLORS.green.D30 }} />
        ) : null,
    },

    {
      headerName: "Role",
      field: "role",
      width: 200,
      minWidth: 200,
      renderCell: ({ row }) => {
        const count: number = row.role;
        // When truncated the true count is higher, so always pluralize.
        const noun = pluralize("Role", row.hasMoreRoles ? 2 : count);
        return <div>{`${count}${row.hasMoreRoles ? "+" : ""} ${noun}`}</div>;
      },
    },

    {
      headerName: "Target",
      field: "target",
      type: "string",
      minWidth: 150,
      sortable: false,
    },

    {
      headerName: "Description",
      field: "description",
      width: 300,
      minWidth: 200,
      sortable: false,
      renderCell: (params) => (
        <TruncatedText
          tooltipTitle={params.row.description}
          value={params.row.description}
        />
      ),
    },

    {
      headerName: "",
      field: "actions",
      width: 60,
      minWidth: 60,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => {
        const { uuid } = params.row;
        return (
          <div className="flex items-center">
            {canManageGroups ? (
              <Link
                textDecoration="none"
                to={`/settings/access-control/groups/${encodeURIComponent(uuid)}/edit`}
              >
                <span className="text-legacy-neutrals-ash">
                  <EditIcon className="h-4 w-4" />
                </span>
              </Link>
            ) : null}
          </div>
        );
      },
    },
  ];

  const roleAssignmentsColumns: GridColDef[] = [
    {
      headerName: "Expand",
      field: "",
      width: 50,
      minWidth: 50,
    },
    {
      headerName: "Group",
      field: "name",
      // Widened by ~5px so the Role/Target/Description columns line up under
      // their main-table headers when expanded.
      type: "string",
      width: 255,
      minWidth: 255,
    },
    // Spacer aligning the detail rows with the main table's SSO column.
    {
      headerName: "SSO",
      field: "sso",
      width: 80,
      minWidth: 80,
    },
    {
      headerName: "Role",
      field: "role",
      type: "string",
      width: 195,
      minWidth: 195,
      renderCell: ({ value }) => {
        return (
          <span className="text-label-extra-small-strong uppercase text-legacy-neutrals-ash">
            {value}
          </span>
        );
      },
    },
    {
      headerName: "Target",
      field: "target",
      width: 175,
      minWidth: 175,
      renderCell: ({ value: targets }) => {
        return <RbacTargetChipGroup targets={targets} />;
      },
    },
    {
      headerName: "Description",
      field: "description",
      type: "string",
      width: 205,
      minWidth: 205,
      renderCell: ({ value }) => {
        return (
          <span className="text-body-small text-legacy-neutrals-ash">
            <span className="font-bold">{value}</span> permission on target(s)
          </span>
        );
      },
    },
  ];

  // build one row per user group, grouping its role assignments by role
  function transformUserGroups(data: GroupsTable_query$data) {
    const rows = (data.userGroups.edges ?? [])
      .filter(
        (edge): edge is { node: NonNullable<UserGroupNode> } => !!edge.node,
      )
      .map(({ node }, index) => {
        const groupedByRole = new Map<string, RoleAssignmentRow>();

        for (const edge of node.roleAssignments.edges ?? []) {
          const ra = edge.node;
          const roleName = ra.role.name;
          const targetStr = `${ra.target?.__typename ?? ""}:${ra.target?.roleAssignmentTarget?.split(":")[1] ?? ""}`;

          if (!groupedByRole.has(roleName)) {
            groupedByRole.set(roleName, {
              id: `role-assignment-${roleName}-${index}`,
              name: "",
              role: roleName,
              targetList: [],
              description: (ra.role.permissions ?? []).join(", "),
            });
          }

          groupedByRole.get(roleName)?.targetList.push(targetStr);
        }

        const roleAssignmentsRows = Array.from(groupedByRole.values()).map(
          (entry, raIndex) => ({
            id: `${entry.id}-${raIndex}`,
            name: "",
            role: entry.role,
            target: entry.targetList.filter(Boolean),
            description: entry.description,
          }),
        );

        // pageInfo.total is best-effort (nullable); fall back to inferring
        // truncation from a full page of fetched edges.
        const fetched = node.roleAssignments.edges?.length ?? 0;
        const total = node.roleAssignments.pageInfo.total;
        const hasMoreRoles =
          total != null
            ? total > fetched
            : fetched >= ROLE_ASSIGNMENTS_PAGE_SIZE;

        return {
          id: `principal-${node.name}${index}`,
          uuid: node.uuid,
          name: node.name,
          displayName: node.displayName || node.name,
          sso: node.source != null,
          role: roleAssignmentsRows.length,
          hasMoreRoles,
          target: "",
          description: "",
          roleAssignments: roleAssignmentsRows,
        };
      });

    return rows;
  }

  const rows = transformUserGroups(data);

  function DetailPanelContent({ row }: { row: GroupRow }) {
    const { roleAssignments, name, hasMoreRoles } = row;
    return (
      <>
        <XGridTable
          columns={roleAssignmentsColumns}
          hasToolbar={false}
          initialState={{
            columns: {
              orderedFields: [
                "expand",
                "name",
                "sso",
                "role",
                "target",
                "description",
              ],
            },
          }}
          rows={roleAssignments}
          slots={{
            columnHeaders: () => null,
          }}
          sx={{
            ".MuiDataGrid-row": {
              backgroundColor: "rgba(var(--background-primary)) !important",
            },

            marginTop: 0,
          }}
          tableId={`sso-group-table-${name}`}
          disableColumnPinning
          disableColumnReorder
          disableColumnResize
          disableColumnSelector
          hideFooter
        />
        {/* This panel only shows the first page of assignments; the edit view
         * iterates the full connection. Flag that more exist rather than
         * implying this list is complete. Indented to align under the Role
         * column (Expand 50 + Name 250 + SSO 80 spacers). */}
        {hasMoreRoles ? (
          <div
            className="pb-md text-label-extra-small-strong uppercase text-legacy-neutrals-ash"
            style={{ paddingLeft: pxToRem(380) }}
          >
            + more roles
          </div>
        ) : null}
      </>
    );
  }

  const getDetailPanelContent = useCallback<
    NonNullable<DataGridProProps["getDetailPanelContent"]>
  >(
    // Returning null hides the disclosure toggle for groups with no roles,
    // since there is nothing to render in the detail panel.
    ({ row }) =>
      row.roleAssignments.length > 0 ? <DetailPanelContent row={row} /> : null,
    [],
  );

  const [expandedRowIds, setExpandedRowIds] = useState<GridRowId[]>();

  useEffect(() => {
    if (expanded) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setExpandedRowIds(
        rows.filter((r) => r.roleAssignments.length > 0).map((r) => r.id),
      );
    } else {
      setExpandedRowIds([]);
    }
  }, [expanded, data]);

  const rowCount = rows.length;
  return (
    <XGridTable
      autosizeOnMount={false}
      columns={columns}
      detailPanelExpandedRowIds={expandedRowIds}
      getDetailPanelContent={getDetailPanelContent}
      getDetailPanelHeight={() => "auto"}
      hasToolbar={false}
      initialState={{
        sorting: {
          sortModel: [{ field: "dynamicFilter", sort: "asc" }],
        },
        columns: {
          orderedFields: [
            "__detail_panel_toggle__",
            "name",
            "sso",
            "role",
            "target",
            "description",
            "actions",
          ],
        },
        pinnedColumns: {
          right: ["actions"],
        },
      }}
      isRowSelectable={(params) => params.row.dynamicFilter == null}
      loading={isLoadingNext}
      onDetailPanelExpandedRowIdsChange={(ids) => setExpandedRowIds(ids)}
      onRowsScrollEnd={loadMore}
      onSortModelChange={setSortModel}
      rowCount={rowCount}
      rows={rows}
      slots={{
        detailPanelExpandIcon: () => (
          <CaretRightIcon className="rotate-90 text-text-secondary" />
        ),
        detailPanelCollapseIcon: () => (
          <CaretRightIcon className="-rotate-90 text-text-secondary" />
        ),
      }}
      sortModel={sortModel || []}
      sx={{
        ".MuiDataGrid-row": {
          backgroundColor: "rgba(var(--background-secondary))",
          borderLeft: "none !important",
          borderRight: "none !important",
        },
        // Groups with no roles have no detail panel, so MUI disables their
        // toggle button. Hide it so the caret doesn't appear clickable.
        "& .MuiDataGrid-detailPanelToggleCell.Mui-disabled": {
          visibility: "hidden",
        },
        ".MuiDataGrid-row--detailPanelExpanded": {
          backgroundColor: "rgba(var(--background-primary))",
          ".MuiDataGrid-cell": {
            borderBottom: `1px solid rgba(var(--outline-decorative))`,
          },
        },
        "& .MuiDataGrid-columnsHeaders": {
          minHeight: `${pxToRem(44)} !important`,
        },
        "& .MuiDataGrid-columnHeader": {
          minHeight: `${pxToRem(44)} !important`,
          paddingTop: `${pxToRem(12)} !important`,
          paddingBottom: `${pxToRem(12)} !important`,
          height: `${pxToRem(44)} !important`,
        },
        "& .MuiDataGrid-columnHeaderTitle": {
          lineHeight: `${pxToRem(20)} !important`,
        },
        "& .MuiDataGrid-columnHeaderTitleContainer": {
          paddingTop: `${pxToRem(12)} !important`,
          paddingBottom: `${pxToRem(12)} !important`,
          height: `${pxToRem(44)} !important`,
        },
        // Pinned column headers get a drop shadow by default; remove it so the
        // pinned actions header matches the rest of the header row.
        "& .MuiDataGrid-pinnedColumnHeaders": {
          boxShadow: "none",
        },
      }}
      tableId="groups-table"
      disableColumnMenu
      disableColumnReorder
      disableColumnResize
      disableColumnSelector
      disableDensitySelector
      disableRowSelectionOnClick
    />
  );
}
